import { computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

/**
 * Composable for handling contact custom attributes
 * @param {Object} contact - The contact object
 * @returns {Object} Contact attributes utilities
 */
export function useContactAttributes(contact) {
  const getAttributesByModel = useMapGetter('attributes/getAttributesByModel');

  /**
   * Custom attributes from contact
   */
  const customAttributes = computed(() => {
    return contact.value?.custom_attributes || {};
  });

  /**
   * Contact attribute definitions from store
   */
  const contactAttributes = computed(() => {
    return getAttributesByModel.value('contact_attribute') || [];
  });

  /**
   * Check if contact has custom attributes
   */
  const hasCustomAttributes = computed(() => {
    return (
      Object.keys(customAttributes.value).length > 0 ||
      contactAttributes.value.length > 0
    );
  });

  /**
   * Attributes with metadata combined
   */
  const attributesWithMetadata = computed(() => {
    const result = [];

    // Get defined attributes with their metadata
    contactAttributes.value.forEach(attr => {
      const key = attr.attribute_key;
      const hasValue = Object.prototype.hasOwnProperty.call(
        customAttributes.value,
        key
      );
      const value = hasValue ? customAttributes.value[key] : '';

      result.push({
        key,
        value,
        attribute_display_name: attr.attribute_display_name || key,
        attribute_display_type: attr.attribute_display_type || 'text',
        attribute_description: attr.attribute_description || '',
        attribute_values: attr.attribute_values || [],
        attribute_key: key,
        regex_pattern: attr.regex_pattern || null,
        regex_cue: attr.regex_cue || null,
      });
    });

    // Add any additional custom attributes not defined in the model
    Object.entries(customAttributes.value).forEach(([key, value]) => {
      if (!result.some(item => item.key === key)) {
        result.push({
          key,
          value,
          attribute_display_name: key,
          attribute_display_type: 'text',
          attribute_description: '',
          attribute_values: [],
          attribute_key: key,
          regex_pattern: null,
          regex_cue: null,
        });
      }
    });

    return result;
  });

  /**
   * All custom attributes as flat object
   */
  const allCustomAttributes = computed(() => {
    const result = {};
    attributesWithMetadata.value.forEach(attr => {
      result[attr.key] = attr.value;
    });
    return result;
  });

  /**
   * Attribute display names mapping
   */
  const attributeDisplayNames = computed(() => {
    const displayMap = {};
    contactAttributes.value.forEach(attr => {
      displayMap[attr.attribute_key] = attr.attribute_display_name;
    });
    return displayMap;
  });

  /**
   * Attribute descriptions mapping
   */
  const attributeDescriptions = computed(() => {
    const descMap = {};
    contactAttributes.value.forEach(attr => {
      descMap[attr.attribute_key] = attr.attribute_description;
    });
    return descMap;
  });

  /**
   * Get attribute display type
   */
  function getAttributeDisplayType(key) {
    const attr = contactAttributes.value.find(
      attr => attr.attribute_key === key
    );
    return attr ? attr.attribute_display_type : 'text';
  }

  /**
   * Format attribute value based on type
   */
  function getFormattedAttributeValue(key, value) {
    if (!value) return '---';

    const type = getAttributeDisplayType(key);

    // Format based on attribute type
    if (type === 'date') {
      return new Date(value).toLocaleDateString();
    }
    if (type === 'checkbox') {
      return value === true || value === 'true' ? 'Sim' : 'Não';
    }
    if (type === 'link') {
      return value; // Links will be handled in the template
    }
    return value;
  }

  /**
   * Update custom attribute
   */
  async function updateCustomAttribute(key, value, store) {
    if (!contact.value?.id) return;

    const updatedAttributes = { ...customAttributes.value, [key]: value };

    try {
      await store.dispatch('contacts/update', {
        id: contact.value.id,
        custom_attributes: updatedAttributes,
      });
      useAlert('Atributo atualizado com sucesso');
    } catch (error) {
      const errorMessage =
        error?.response?.message || 'Erro ao atualizar o atributo';
      useAlert(errorMessage, 'error');
    }
  }

  /**
   * Delete custom attribute
   */
  async function deleteCustomAttribute(key, store) {
    if (!contact.value?.id) return;

    try {
      await store.dispatch('contacts/deleteCustomAttributes', {
        id: contact.value.id,
        customAttributes: [key],
      });
      useAlert('Atributo excluído com sucesso');
    } catch (error) {
      const errorMessage =
        error?.response?.message || 'Erro ao excluir o atributo';
      useAlert(errorMessage, 'error');
    }
  }

  return {
    customAttributes,
    contactAttributes,
    hasCustomAttributes,
    attributesWithMetadata,
    allCustomAttributes,
    attributeDisplayNames,
    attributeDescriptions,
    getAttributeDisplayType,
    getFormattedAttributeValue,
    updateCustomAttribute,
    deleteCustomAttribute,
  };
}